import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  teamsApi,
  type CreateTeamInput,
  type UpdateTeamInput,
  type CreateStatusInput,
  type UpdateStatusInput,
} from '@/lib/api/teams';

export const teamKeys = {
  all: ['teams'] as const,
  lists: () => [...teamKeys.all, 'list'] as const,
  tree: () => [...teamKeys.all, 'tree'] as const,
  epicOwners: () => [...teamKeys.all, 'epicOwners'] as const,
  details: () => [...teamKeys.all, 'detail'] as const,
  detail: (id: string) => [...teamKeys.details(), id] as const,
  children: (id: string) => [...teamKeys.detail(id), 'children'] as const,
  descendants: (id: string) => [...teamKeys.detail(id), 'descendants'] as const,
  ancestors: (id: string) => [...teamKeys.detail(id), 'ancestors'] as const,
  members: (id: string) => [...teamKeys.detail(id), 'members'] as const,
  statuses: (id: string) => [...teamKeys.detail(id), 'statuses'] as const,
};

export function useTeams() {
  return useQuery({
    queryKey: teamKeys.lists(),
    queryFn: () => teamsApi.list(),
  });
}

export function useTeamTree() {
  return useQuery({
    queryKey: teamKeys.tree(),
    queryFn: () => teamsApi.tree(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEpicOwnerTeams() {
  return useQuery({
    queryKey: teamKeys.epicOwners(),
    queryFn: () => teamsApi.epicOwners(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
  });
}

export function useTeam(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: () => teamsApi.get(id),
    enabled: (options?.enabled ?? true) && !!id,
    select: (response) => response.data,
  });
}

export function useTeamChildren(teamId: string) {
  return useQuery({
    queryKey: teamKeys.children(teamId),
    queryFn: () => teamsApi.getChildren(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamDescendants(teamId: string) {
  return useQuery({
    queryKey: teamKeys.descendants(teamId),
    queryFn: () => teamsApi.getDescendants(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamAncestors(teamId: string) {
  return useQuery({
    queryKey: teamKeys.ancestors(teamId),
    queryFn: () => teamsApi.getAncestors(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamMembers(teamId: string) {
  return useQuery({
    queryKey: teamKeys.members(teamId),
    queryFn: () => teamsApi.getMembers(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

export function useTeamStatuses(teamId: string) {
  return useQuery({
    queryKey: teamKeys.statuses(teamId),
    queryFn: () => teamsApi.getStatuses(teamId),
    enabled: !!teamId,
    select: (response) => response.data,
  });
}

function invalidateTeamCaches(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: teamKeys.tree() });
  void queryClient.invalidateQueries({ queryKey: teamKeys.details() });
  void queryClient.invalidateQueries({ queryKey: teamKeys.epicOwners() });
}

export function useCreateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTeamInput) => teamsApi.create(input),
    onSuccess: () => {
      invalidateTeamCaches(queryClient);
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateTeamInput) => teamsApi.update(input),
    onSuccess: (response) => {
      invalidateTeamCaches(queryClient);
      queryClient.setQueryData(teamKeys.detail(response.data.id), { data: response.data });
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => teamsApi.delete(id),
    onSuccess: (_response, id) => {
      invalidateTeamCaches(queryClient);
      queryClient.removeQueries({ queryKey: teamKeys.detail(id) });
    },
  });
}

export function useReparentTeam() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: teamsApi.reparent,
    onSuccess: (response) => {
      invalidateTeamCaches(queryClient);
      queryClient.setQueryData(teamKeys.detail(response.data.id), { data: response.data });
    },
  });
}

export function useAddTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      teamsApi.addMember(teamId, userId),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) });
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      teamsApi.removeMember(teamId, userId),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) });
      void queryClient.invalidateQueries({ queryKey: teamKeys.detail(teamId) });
    },
  });
}

export function useCreateTeamStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, ...input }: CreateStatusInput & { teamId: string }) =>
      teamsApi.createStatus(teamId, input),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.statuses(teamId) });
    },
  });
}

export function useUpdateTeamStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamId,
      statusId,
      ...input
    }: UpdateStatusInput & { teamId: string; statusId: string }) =>
      teamsApi.updateStatus(teamId, statusId, input),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.statuses(teamId) });
    },
  });
}

export function useDeleteTeamStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ teamId, statusId }: { teamId: string; statusId: string }) =>
      teamsApi.deleteStatus(teamId, statusId),
    onSuccess: (_, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: teamKeys.statuses(teamId) });
    },
  });
}
